/* @flow */
import AuthActions from 'auth/AuthActions';
import BoxBody from 'components/BoxBody.jsx';
import CenteredContainer from 'components/CenteredContainer.jsx';
import Columns2 from 'components/Columns2.jsx';
import DialogContent from 'components/DialogContent.jsx';
import enumerate from 'lib/enumerate';
import Image from 'components/Image.jsx';
import JoinOrganizationAvatarPanel from './JoinOrganizationAvatarPanel.jsx';
import JoinOrganizationFormPanel from './JoinOrganizationFormPanel.jsx';
import JoinPageLoadingIndicator from './JoinPageLoadingIndicator.jsx';
import OrganizationProfile from 'entities/OrganizationProfile';
import PropTypes from 'prop-types';
import * as React from 'react';
import { Roles } from 'user/UserConstants';
import TitleHeader from 'components/TitleHeader.jsx';
import invariant from 'invariant';

import type { RouterState, LocationDescriptor } from 'lib/ReactRouterTypes';

import './styles/JoinOrganizationPage.scss';

const STATUS = enumerate(['loading', 'success', 'failure']);

type Props = {
  location: Object,
  params: Object,
};

type State = {
  status: number,
  organization: ?OrganizationProfile,
  query: Object,
};

class JoinOrganizationPage extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  static onEnter(
    nextState: RouterState,
    replaceWith: (location: LocationDescriptor) => void,
    callback: Function
  ) {
    const onSuccess = () => {
      replaceWith(`/school/confirm/${nextState.params.id}`);
      callback();
    };

    AuthActions.checkLoggedInAndLoadAppData().then(onSuccess, () => callback());
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      status: STATUS.loading,
      organization: null,
      query: props.location.query,
    };
  }

  componentDidMount() {
    this.fetchOrganization();
  }

  componentWillReceiveProps(nextProps: Object) {
    if (this.props.params.id !== nextProps.params.id) {
      this.fetchOrganization();
    }
  }

  fetchOrganization = () => {
    const onSuccess = organization => {
      this.setState({
        status: STATUS.success,
        organization,
      });
    };

    const onFailure = () => {
      this.setState({ status: STATUS.failure });
    };

    const organizationId = this.props.params.id;

    OrganizationProfile.fetch({ id: organizationId }).then(onSuccess, onFailure);
  };

  renderFailure = () => {
    return (
      <CenteredContainer className="JoinOrganizationPage-ErrorPane">
        <BoxBody>
          <Image
            className="NotFoundPage-image"
            src={require('assets/images/emoji-grin.svg')}
            alt="This is embarrassing"
          />
          <div className="NotFoundPage-title">Uh oh! Something went wrong.</div>
          <p>We could not find this school.</p>
        </BoxBody>
      </CenteredContainer>
    );
  };

  renderLoading = () => {
    return <JoinPageLoadingIndicator />;
  };

  renderSuccess = () => {
    invariant(this.state.organization, 'organization required in renderSuccess');

    return (
      <DialogContent size="large" className="JoinOrganizationPage" onRequestHide={() => {}}>
        <BoxBody>
          <Columns2 flex reset="tablet" resetFlush className="JoinOrganizationPage-columns">
            <JoinOrganizationAvatarPanel organization={this.state.organization} />

            <div>
              <TitleHeader
                title={`Sign up for Remind at ${this.state.organization.getName()}`}
                className="JoinOrganizationPage-title"
              />
              <JoinOrganizationFormPanel
                role={this.state.query.role || Roles.teacher}
                defaultValues={this.state.query}
                organization={this.state.organization}
              />
            </div>
          </Columns2>
        </BoxBody>
      </DialogContent>
    );
  };

  render() {
    return {
      [STATUS.loading]: this.renderLoading,
      [STATUS.success]: this.renderSuccess,
      [STATUS.failure]: this.renderFailure,
    }[this.state.status]();
  }
}

export default JoinOrganizationPage;
